import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme, ThemeToggle } from '../../contexts/ThemeContext';
import { ThemePresetGallery, ThemePreviewMode } from '../Theme';
import Icons from './Icons';
import NotificationBell from './NotificationBell';
import InstallAppButton from './InstallAppButton';

const Sidebar = ({ currentPage, setCurrentPage, showToast }) => {
  const { user, logout } = useAuth();
  const { isDarkMode } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showThemeGallery, setShowThemeGallery] = useState(false);
  const [showThemePreview, setShowThemePreview] = useState(false);
  const [editingNavId, setEditingNavId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');

  // Custom page names stored in localStorage
  const [customLabels, setCustomLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('infopilot_custom_labels') || '{}'); }
    catch { return {}; }
  });

  const saveCustomLabel = (pageId) => {
    const trimmed = editingLabel.trim();
    if (trimmed && trimmed.length <= 30) {
      const updated = { ...customLabels, [pageId]: trimmed };
      setCustomLabels(updated);
      localStorage.setItem('infopilot_custom_labels', JSON.stringify(updated));
      showToast && showToast(`Page renamed to "${trimmed}"`, 'success');
    }
    setEditingNavId(null);
  };

  const resetLabel = (pageId) => {
    const updated = { ...customLabels };
    delete updated[pageId];
    setCustomLabels(updated);
    localStorage.setItem('infopilot_custom_labels', JSON.stringify(updated));
    setEditingNavId(null);
  };

  const navItems = [
    { id: 'search', label: customLabels['search'] || 'Ultimate Search', icon: Icons.Search },
    { id: 'marketplace', label: customLabels['marketplace'] || 'Marketplace', icon: Icons.Shop },
    { id: 'templates', label: '📋 Templates', icon: Icons.Document },
    { id: 'statistics', label: '📊 Statistics', icon: Icons.Chart },
    { id: 'cockpit', label: 'Chat', icon: Icons.Message },
    { id: 'achievements', label: 'Achievements', icon: Icons.Trophy },
    { id: 'social', label: 'Social', icon: Icons.Users },
    { id: 'map', label: 'Map View', icon: Icons.Map },
    { id: 'quotes', label: 'Author Quotes', icon: Icons.Quote },
    { id: 'messages', label: 'Messages', icon: Icons.Message },
    { id: 'tutorials', label: '🎬 Tutorials', icon: Icons.Play },
    { id: 'task-history', label: 'Task History', icon: Icons.Clock },
    { id: 'search-analytics', label: 'Search Analytics', icon: Icons.BarChart },
    { id: 'advanced-analytics', label: 'Analytics Dashboard', icon: Icons.Chart },
    { id: 'collaboration', label: 'Collaboration', icon: Icons.Users },
    { id: 'profile', label: 'My Profile', icon: Icons.Users },
    { id: 'donation', label: 'Support Us', icon: Icons.Heart },
    { id: 'settings', label: 'Settings', icon: Icons.Settings },
  ];

  const handleNavClick = (pageId) => {
    setCurrentPage(pageId);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between" style={{ background: '#ffffff', borderBottom: '1px solid #E2E8F0' }}>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2"
          style={{ color: '#475569' }}
          data-testid="mobile-menu-btn"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
          </svg>
        </button>
        <h1 className="text-lg font-bold bg-clip-text text-transparent" style={{ fontFamily: 'Outfit, sans-serif', backgroundImage: 'linear-gradient(135deg, #0EA5E9, #10B981)' }}>InfoPilot</h1>
        <NotificationBell showToast={showToast} />
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`} data-testid="sidebar">
        <div className="sidebar-logo flex items-center justify-between" style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Living landscape — sun + drifting clouds behind the wordmark */}
          <svg
            aria-hidden="true"
            width="100%" height="52"
            viewBox="0 0 240 52"
            preserveAspectRatio="none"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              pointerEvents: 'none', zIndex: 0, opacity: 0.75,
            }}
          >
            {/* Sun */}
            <circle cx="210" cy="14" r="7" fill="#FCD34D" opacity="0.9">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="4s" repeatCount="indefinite" />
            </circle>
            {/* Drifting cloud 1 */}
            <g opacity="0.85">
              <ellipse cx="0" cy="16" rx="14" ry="5" fill="#ffffff" />
              <ellipse cx="8" cy="13" rx="9" ry="4" fill="#ffffff" />
              <animateTransform attributeName="transform" type="translate"
                from="-20 0" to="260 0" dur="30s" repeatCount="indefinite" />
            </g>
            {/* Drifting cloud 2 */}
            <g opacity="0.7">
              <ellipse cx="0" cy="34" rx="10" ry="4" fill="#ffffff" />
              <ellipse cx="5" cy="32" rx="7" ry="3" fill="#ffffff" />
              <animateTransform attributeName="transform" type="translate"
                from="-30 0" to="270 0" dur="45s" begin="-10s" repeatCount="indefinite" />
            </g>
            {/* Tiny bird — gentle sinusoidal bob */}
            <g opacity="0.55">
              <path d="M 0 25 q 2 -2 4 0 q 2 -2 4 0" stroke="#047857" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              <animateTransform attributeName="transform" type="translate"
                from="-10 0" to="260 -6" dur="22s" begin="-3s" repeatCount="indefinite" />
            </g>
          </svg>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <h1>InfoPilot</h1>
            <p>Information Exchange Network</p>
          </div>
          {/* Desktop Notification Bell */}
          <div className="hidden md:block" style={{ position: 'relative', zIndex: 1 }}>
            <NotificationBell showToast={showToast} />
          </div>
        </div>
        
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">
            {user?.username?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="sidebar-user-info">
            <h3>
              {user?.username}
              {user?.is_admin && <span className="admin-badge">ADMIN</span>}
              {!user?.is_admin && user?.is_paid && <span className="full-access-badge">FULL ACCESS</span>}
            </h3>
            <p>{user?.email}</p>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* ADMIN CONTROL - Bright Sticker - Only for Admin */}
          {user?.is_admin && (
            <div
              className={`sidebar-nav-item admin-control-link ${currentPage === 'admin' ? 'active' : ''}`}
              onClick={() => handleNavClick('admin')}
              data-testid="admin-nav-item"
            >
              Admin Control
            </div>
          )}
          
          {/* Analytics - Available to all logged-in users */}
          {user && (
            <div
              className={`sidebar-nav-item ${currentPage === 'analytics' ? 'active' : ''}`}
              onClick={() => handleNavClick('analytics')}
              data-testid="nav-analytics"
              style={{
                background: currentPage === 'analytics' 
                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.3), rgba(16, 185, 129, 0.2))' 
                  : 'transparent',
                borderLeft: currentPage === 'analytics' ? '3px solid #3b82f6' : 'none'
              }}
            >
              <Icons.Chart />
              <span>📊 Analytics</span>
            </div>
          )}

          {navItems.map(item => (
            <div
              key={item.id}
              className={`sidebar-nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => { if (editingNavId !== item.id) handleNavClick(item.id); }}
              onDoubleClick={(e) => { e.preventDefault(); setEditingNavId(item.id); setEditingLabel(item.label); }}
              data-testid={`nav-${item.id}`}
              title="Double-click to rename this page"
            >
              <item.icon />
              {editingNavId === item.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }} onClick={e => e.stopPropagation()}>
                  <input
                    value={editingLabel}
                    onChange={e => setEditingLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveCustomLabel(item.id); if (e.key === 'Escape') setEditingNavId(null); }}
                    autoFocus
                    maxLength={30}
                    data-testid={`rename-input-${item.id}`}
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(192,132,252,0.4)', color: '#334155', padding: '2px 6px', borderRadius: 4, fontSize: '0.75rem', width: '100%', outline: 'none' }}
                    placeholder="Page name (max 30 chars)"
                  />
                  <button onClick={() => saveCustomLabel(item.id)} style={{ background: 'rgba(16,185,129,0.2)', border: 'none', color: '#34d399', padding: '2px 4px', borderRadius: 3, cursor: 'pointer', fontSize: '0.6rem' }} title="Save">OK</button>
                  {customLabels[item.id] && <button onClick={() => resetLabel(item.id)} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', color: '#f87171', padding: '2px 4px', borderRadius: 3, cursor: 'pointer', fontSize: '0.55rem' }} title="Reset to default">Reset</button>}
                </div>
              ) : (
                <span>{item.label}</span>
              )}
            </div>
          ))}

          <div className="sidebar-nav-item" onClick={logout} style={{ marginTop: 'auto' }} data-testid="logout-btn">
            <Icons.LogOut />
            Logout
          </div>
          
          {/* Theme Controls - Dark Mode + Accent Color + Gallery + Preview */}
          <div style={{ padding: '10px 0', borderTop: '1px solid rgba(252, 211, 77, 0.2)', marginTop: 10 }}>
            <ThemeToggle 
              showColorPicker={true} 
              onOpenGallery={() => setShowThemeGallery(true)}
              onOpenPreview={() => setShowThemePreview(true)}
            />
          </div>
        </nav>
        
        {/* Book Promo - Enhanced with accurate details */}
        <div style={{ marginTop: 20, padding: 15, background: 'linear-gradient(135deg, rgba(134, 239, 172, 0.25), rgba(186, 230, 253, 0.25))', borderRadius: 12, border: '2px solid rgba(16, 185, 129, 0.4)' }}>
          <div style={{ fontSize: '0.85rem', color: '#047857', fontWeight: 800, marginBottom: 8, textAlign: 'center' }}>
            OPTIONED FOR FILM
          </div>
          <div style={{ fontSize: '0.9rem', color: '#0F172A', marginBottom: 6, textAlign: 'center', fontWeight: 700 }}>
            "Letters to Evelyn"
          </div>
          <div style={{ fontSize: '0.7rem', color: '#0369A1', marginBottom: 8, fontStyle: 'italic', textAlign: 'center' }}>
            Supernatural Thriller Comedy Memoir
          </div>
          <div style={{ fontSize: '0.65rem', color: '#059669', marginBottom: 6, textAlign: 'center', fontWeight: 600 }}>
            19 Five-Star Reviews | BlueInk Notable Book Award
          </div>
          <div style={{ fontSize: '0.6rem', color: '#64748B', marginBottom: 10, fontStyle: 'italic', textAlign: 'center', lineHeight: 1.3 }}>
            by John Selman — Son of an elite U.S. Navy pilot
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <a href="https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US" target="_blank" rel="noopener noreferrer"
              className="btn btn-primary" data-testid="sidebar-book-link"
              style={{ fontSize: '0.78rem', padding: '8px 10px', display: 'block', textAlign: 'center', textDecoration: 'none', background: 'linear-gradient(135deg, #10B981, #059669)', color: '#ffffff', boxShadow: '0 0 15px rgba(16, 185, 129, 0.4)' }}>
              <i className="fas fa-shopping-cart" style={{ marginRight: 4 }} /> Buy on Google Play Books
            </a>
            <a href="https://drive.google.com/file/d/1UhDwAs9VwUUoIinRNwnHFYZ0RlcvMBQI/view?usp=sharing" target="_blank" rel="noopener noreferrer"
              data-testid="sidebar-free-read-link"
              style={{ fontSize: '0.72rem', padding: '6px 8px', display: 'block', textAlign: 'center', textDecoration: 'none', borderRadius: 8, background: 'rgba(14, 165, 233, 0.15)', border: '1px solid rgba(14, 165, 233, 0.35)', color: '#0369A1' }}>
              <i className="fas fa-gift" style={{ marginRight: 4 }} /> Read Entire Book FREE
            </a>
          </div>
          {/* Install App Prompt */}
          <div style={{ padding: '8px 0 0' }}>
            <InstallAppButton variant="inline" />
          </div>
          {/* Social Media Links in Sidebar */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <a href="https://www.facebook.com/profile.php?id=61584845722433" target="_blank" rel="noopener noreferrer" style={{ color: '#4267B2', fontSize: '0.85rem' }} title="Letters to Evelyn on Facebook">
              <i className="fab fa-facebook" />
            </a>
            <a href="https://www.instagram.com/jjspilot76" target="_blank" rel="noopener noreferrer" style={{ color: '#E1306C', fontSize: '0.85rem' }} title="Instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="https://www.linkedin.com/in/john-selman-aa048411" target="_blank" rel="noopener noreferrer" style={{ color: '#0A66C2', fontSize: '0.85rem' }} title="LinkedIn">
              <i className="fab fa-linkedin" />
            </a>
            <a href="https://www.pinterest.com/selman24" target="_blank" rel="noopener noreferrer" style={{ color: '#E60023', fontSize: '0.85rem' }} title="Pinterest">
              <i className="fab fa-pinterest" />
            </a>
            <a href="https://www.youtube.com/@johnselman4221" target="_blank" rel="noopener noreferrer" style={{ color: '#FF0000', fontSize: '0.85rem' }} title="YouTube">
              <i className="fab fa-youtube" />
            </a>
          </div>
        </div>
      </div>

      {/* Theme Gallery Modal */}
      {showThemeGallery && (
        <ThemePresetGallery 
          showToast={showToast} 
          onClose={() => setShowThemeGallery(false)} 
        />
      )}
      
      {/* Theme Preview Modal */}
      {showThemePreview && (
        <ThemePreviewMode 
          showToast={showToast} 
          onClose={() => setShowThemePreview(false)} 
        />
      )}
    </>
  );
};

export default Sidebar;
